import { useEffect, useMemo, useState, Fragment } from "react";
import { api, formatBRL, formatDateTimeBR } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { spDateKey } from "../../lib/datetime";
import {
  Calendar, Clock, User, MapPin, ChevronDown, ChevronRight,
  List as ListIcon, CalendarDays, ChevronLeft, ChevronRight as ChevRight,
  Mail, Phone, FileText, Home, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import ExportButtons from "../../components/ExportButtons";

// ────────── RC10: badges oficiais da máquina de estados ──────────
const STATUS_LABELS = {
  pending: "Pendente", pending_payment: "Aguardando pagamento",
  confirmed: "Confirmada", in_progress: "Em andamento", completed: "Concluída",
  payment_expired: "Pagamento expirado", expired: "Pagamento expirado",
  cancelled: "Cancelada", cancelled_by_admin: "Cancelada pela administração",
  no_show: "Não compareceu",
};
const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700",
  pending_payment: "bg-amber-50 text-amber-700",
  confirmed: "bg-blue-50 text-blue-700",
  in_progress: "bg-emerald-50 text-emerald-700",
  completed: "bg-gray-100 text-gray-600",
  payment_expired: "bg-red-50 text-red-700",
  expired: "bg-red-50 text-red-700",
  cancelled: "bg-gray-700 text-white",
  cancelled_by_admin: "bg-orange-100 text-orange-700",
  no_show: "bg-red-900 text-white",
};
const PAY_LABELS = { paid: "Pago", pending: "Pendente", cancelled: "Cancelado", overdue: "Vencido" };

function formatDurationHours(startIso, endIso) {
  try {
    const s = new Date(startIso), e = new Date(endIso);
    const h = (e - s) / 3_600_000;
    return h % 1 === 0 ? `${h}h` : `${h.toFixed(1).replace(".", ",")}h`;
  } catch { return "—"; }
}

function formatAddress(addr) {
  if (!addr || typeof addr !== "object") return null;
  const parts = [
    [addr.street, addr.number].filter(Boolean).join(", "),
    addr.complement,
    addr.neighborhood,
    [addr.city, addr.state].filter(Boolean).join(" / "),
    addr.cep,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

function maskCPF(cpf) {
  const d = (cpf || "").replace(/\D/g, "");
  if (d.length !== 11) return cpf || "—";
  return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

// ────────── componente ──────────
export default function Reservas() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("list"); // list | calendar
  const [expanded, setExpanded] = useState(null);
  const [calMonth, setCalMonth] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  const load = () => api.get("/reservations").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  // RC10: status é automático — ações manuais restritas a cancelar e check-in
  const { user } = useAuth();
  const canCancel = ["super_admin", "admin", "manager"].includes(user?.role);
  // RC11: exclusão (soft delete) exclusiva do Master
  const isMaster = !!user?.is_master;
  const [deleteTarget, setDeleteTarget] = useState(null);

  const softDelete = async () => {
    try {
      await api.delete(`/reservations/${deleteTarget}`);
      toast.success("Reserva excluída da operação");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir");
    }
  };

  // RC19 — check-out com escolha do meio de cobrança do eventual excedente
  const [checkoutTarget, setCheckoutTarget] = useState(null);
  const [checkoutMethod, setCheckoutMethod] = useState("pix");
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState(null);

  const checkOut = (id) => { setCheckoutMethod("pix"); setCheckoutTarget(id); };

  const confirmCheckOut = async () => {
    setCheckoutBusy(true);
    try {
      const { data } = await api.post(`/reservations/${checkoutTarget}/check-out`, { overage_method: checkoutMethod });
      setCheckoutTarget(null);
      setCheckoutResult(data);
      toast.success("Check-out registrado — reserva concluída");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro no check-out");
    } finally {
      setCheckoutBusy(false);
    }
  };

  const cancelRes = async (id) => {
    if (!window.confirm("Cancelar esta reserva? Horas debitadas serão estornadas.")) return;
    try {
      await api.patch(`/reservations/${id}?status=cancelled`);
      toast.success("Reserva cancelada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cancelar");
    }
  };

  const checkIn = async (id) => {
    try {
      await api.post(`/reservations/${id}/check-in`);
      toast.success("Check-in registrado — reserva em utilização");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro no check-in");
    }
  };

  return (
    <div data-testid="admin-reservas">
      <div className="admin-page-header">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Reservas</div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Reservas de Espaços</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex bg-gray-100 rounded-lg p-1" data-testid="reservas-view-toggle">
            <button
              onClick={() => setView("list")}
              data-testid="reservas-view-list"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-all ${
                view === "list" ? "bg-white text-[#14213D] shadow-sm" : "text-gray-500"
              }`}
            >
              <ListIcon size={13} /> Lista
            </button>
            <button
              onClick={() => setView("calendar")}
              data-testid="reservas-view-calendar"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-all ${
                view === "calendar" ? "bg-white text-[#14213D] shadow-sm" : "text-gray-500"
              }`}
            >
              <CalendarDays size={13} /> Calendário
            </button>
          </div>
          <ExportButtons resource="reservas" />
        </div>
      </div>

      {view === "list" ? (
        <ListView
          items={items}
          expanded={expanded}
          setExpanded={setExpanded}
          checkIn={checkIn}
          checkOut={checkOut}
          cancelRes={cancelRes}
          canCancel={canCancel}
          isMaster={isMaster}
          onAskDelete={setDeleteTarget}
        />
      ) : (
        <CalendarView
          items={items}
          calMonth={calMonth}
          setCalMonth={setCalMonth}
        />
      )}

      {checkoutTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="checkout-method-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="font-semibold text-[#14213D] text-lg mb-2">Registrar check-out</h3>
            <p className="text-sm text-gray-600 mb-4">
              O tempo efetivamente utilizado será calculado agora. Se houver excedente
              sobre o contratado, uma cobrança adicional será gerada. Escolha o meio de pagamento:
            </p>
            <div className="space-y-2 mb-5">
              {[["pix", "PIX (aprovação imediata)"], ["cartao", "Cartão de crédito (Checkout Pro)"]].map(([v, label]) => (
                <label key={v} className={`flex items-center gap-2.5 border rounded-lg px-3 py-2.5 cursor-pointer text-sm ${checkoutMethod === v ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-gray-200"}`}>
                  <input
                    type="radio" name="overage-method" value={v}
                    checked={checkoutMethod === v}
                    onChange={() => setCheckoutMethod(v)}
                    data-testid={`overage-method-${v}`}
                  />
                  <span className="text-[#14213D] font-medium">{label}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setCheckoutTarget(null)}
                data-testid="checkout-cancel-btn"
                className="px-4 py-2 text-sm rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
              >Cancelar</button>
              <button
                onClick={confirmCheckOut}
                disabled={checkoutBusy}
                data-testid="checkout-confirm-btn"
                className="px-4 py-2 text-sm rounded bg-[#14213D] text-white font-semibold hover:bg-[#1F335E] disabled:opacity-50"
              >{checkoutBusy ? "Registrando…" : "Confirmar check-out"}</button>
            </div>
          </div>
        </div>
      )}

      {checkoutResult && (
        <CheckoutResultModal result={checkoutResult} onClose={() => setCheckoutResult(null)} />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="delete-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="font-semibold text-[#14213D] text-lg mb-2">Excluir esta reserva?</h3>
            <p className="text-sm text-gray-600 mb-5">
              Esta ação irá ocultar a reserva da operação, mas manterá os registros para auditoria.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                data-testid="delete-cancel-btn"
                className="px-4 py-2 text-sm rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
              >Cancelar</button>
              <button
                onClick={softDelete}
                data-testid="delete-confirm-btn"
                className="px-4 py-2 text-sm rounded bg-red-600 text-white font-semibold hover:bg-red-700"
              >Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────── RC19: resumo do check-out (tempo, banco de horas, excedente) ──────────
const fmtH = (h) => `${Number(h || 0).toFixed(2).replace(".", ",").replace(",00", "")}h`;

function CheckoutResultModal({ result, onClose }) {
  const hb = result.hour_bank;
  const ov = result.overage;
  const copyPix = () => {
    navigator.clipboard.writeText(ov.pix_copy_paste);
    toast.success("Código PIX copiado");
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="checkout-result-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="font-semibold text-[#14213D] text-lg mb-3">Check-out concluído</h3>
        <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm mb-3" data-testid="checkout-time-summary">
          <div className="flex justify-between"><span className="text-gray-500">Tempo utilizado</span><span className="font-bold text-[#14213D]">{fmtH(result.actual_hours_used)}</span></div>
          <div className="flex justify-between mt-1"><span className="text-gray-500">Tempo contratado</span><span className="font-semibold text-gray-700">{fmtH(result.contracted_hours)}</span></div>
        </div>
        {hb && (
          <div className="bg-emerald-50 rounded-lg px-4 py-3 text-sm mb-3" data-testid="checkout-hourbank-summary">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1.5">Banco de Horas{hb.plan_name ? ` · ${hb.plan_name}` : ""}</div>
            <div className="flex justify-between"><span className="text-gray-600">Saldo anterior</span><span className="font-bold text-[#14213D]" data-testid="hb-balance-before">{fmtH(hb.balance_before)}</span></div>
            <div className="flex justify-between mt-1"><span className="text-gray-600">Consumo</span><span className="font-bold text-amber-600" data-testid="hb-consumed-now">−{fmtH(hb.consumed)}</span></div>
            <div className="flex justify-between mt-1"><span className="text-gray-600">Saldo posterior</span><span className="font-bold text-emerald-700" data-testid="hb-balance-after">{fmtH(hb.balance_after)}</span></div>
          </div>
        )}
        {ov ? (
          <div className="bg-amber-50 rounded-lg px-4 py-3 text-sm mb-3" data-testid="checkout-overage-summary">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-1.5">Tempo excedente</div>
            <div className="flex justify-between"><span className="text-gray-600">Excedente</span><span className="font-bold text-[#14213D]">{fmtH(ov.hours)}</span></div>
            <div className="flex justify-between mt-1"><span className="text-gray-600">Cobrança adicional</span><span className="font-bold text-amber-700" data-testid="overage-amount">{formatBRL(ov.amount)}</span></div>
            <div className="flex justify-between mt-1"><span className="text-gray-600">Meio</span><span className="font-semibold text-gray-700">{ov.method === "pix" ? "PIX" : "Cartão (Checkout Pro)"}</span></div>
            {ov.charge_error ? (
              <div className="mt-2 text-xs text-red-600 font-semibold">Falha ao gerar a cobrança no gateway — a fatura ficou pendente no Financeiro.</div>
            ) : (
              <div className="mt-2.5 flex flex-wrap gap-2">
                {ov.payment_link && (
                  <a
                    href={ov.payment_link} target="_blank" rel="noreferrer"
                    data-testid="overage-payment-link"
                    className="text-[11px] font-bold bg-[#14213D] text-white px-3 py-1.5 rounded hover:bg-[#1F335E]"
                  >Abrir cobrança</a>
                )}
                {ov.pix_copy_paste && (
                  <button
                    onClick={copyPix}
                    data-testid="overage-copy-pix"
                    className="text-[11px] font-bold border border-[#14213D] text-[#14213D] px-3 py-1.5 rounded hover:bg-gray-50"
                  >Copiar código PIX</button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-500 mb-3" data-testid="checkout-no-overage">Sem tempo excedente — nenhuma cobrança adicional.</div>
        )}
        <div className="flex justify-end">
          <button
            onClick={onClose}
            data-testid="checkout-result-close"
            className="px-4 py-2 text-sm rounded bg-[#14213D] text-white font-semibold hover:bg-[#1F335E]"
          >Fechar</button>
        </div>
      </div>
    </div>
  );
}

// ────────── LIST VIEW ──────────
function ListView({ items, expanded, setExpanded, checkIn, checkOut, cancelRes, canCancel, isMaster, onAskDelete }) {
  // RC20 — "Hoje" sempre no topo (America/Sao_Paulo); demais dias na ordem cronológica existente (RC11)
  const todayKey = spDateKey(new Date().toISOString());
  const todayLabel = new Date().toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  const byDay = {};
  for (const r of items) {
    try {
      const k = spDateKey(r.start_time);
      (byDay[k] = byDay[k] || []).push(r);
    } catch (err) { console.warn("Data de reserva inválida ignorada:", r.id, err); }
  }
  const dayLabel = (k) => { const [y, m, d] = k.split("-"); return `${d}/${m}/${y}`; };
  const groups = [
    { key: todayKey, label: `Hoje — ${todayLabel}`, isToday: true, rows: byDay[todayKey] || [] },
    ...Object.keys(byDay).filter((k) => k !== todayKey).sort()
      .map((k) => ({ key: k, label: dayLabel(k), isToday: false, rows: byDay[k] })),
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 font-semibold">
            <tr>
              <th className="px-4 py-3 w-6"></th>
              <th className="px-4 py-3">Espaço</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Período</th>
              <th className="px-4 py-3">Duração</th>
              <th className="px-4 py-3">Valor</th>
              <th className="px-4 py-3">Pagto</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.key}>
                <tr className={g.isToday ? "bg-[#14213D]/[0.04]" : "bg-gray-50"} data-testid={g.isToday ? "group-today" : `group-day-${g.key}`}>
                  <td colSpan={9} className={`px-4 py-2 text-[11px] uppercase tracking-wider font-bold ${g.isToday ? "text-[#14213D]" : "text-gray-500"}`}>
                    {g.label}
                  </td>
                </tr>
                {g.isToday && g.rows.length === 0 && (
                  <tr data-testid="group-today-empty">
                    <td colSpan={9} className="px-5 py-4 text-center text-gray-400 text-xs">Nenhuma reserva para hoje.</td>
                  </tr>
                )}
                {g.rows.map((r) => {
              const open = expanded === r.id;
              const payStatus = r.payment_status || r.paymentStatus;
              return (
                <>
                  <tr
                    key={r.id}
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                    data-testid={`reservation-${r.id}`}
                    onClick={() => setExpanded(open ? null : r.id)}
                  >
                    <td className="px-4 py-3 text-gray-400">
                      {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="text-[#D4AF37]" />
                        <span className="font-semibold text-[#14213D]">{r.space_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-400" />
                        {r.guest_name || r.client_name || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      <div className="flex items-center gap-1"><Calendar size={11} />{formatDateTimeBR(r.start_time)}</div>
                      <div className="flex items-center gap-1 mt-0.5"><Clock size={11} />até {formatDateTimeBR(r.end_time)}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-xs">{formatDurationHours(r.start_time, r.end_time)}</td>
                    <td className="px-4 py-3 font-semibold text-[#14213D]">{formatBRL(r.price)}</td>
                    <td className="px-4 py-3">
                      {payStatus ? (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
                          payStatus === "paid" ? "bg-emerald-50 text-emerald-700" :
                          payStatus === "cancelled" ? "bg-red-50 text-red-700" :
                          "bg-amber-50 text-amber-700"
                        }`}>
                          {PAY_LABELS[payStatus] || payStatus}
                        </span>
                      ) : (<span className="text-gray-300 text-xs">—</span>)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${STATUS_STYLE[r.status] || "bg-gray-100 text-gray-500"}`}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        {r.status === "confirmed" && !r.checked_in_at && (
                          <button
                            onClick={() => checkIn(r.id)}
                            className="text-[11px] font-bold bg-emerald-600 text-white px-2.5 py-1 rounded hover:bg-emerald-700"
                            data-testid={`checkin-${r.id}`}
                          >Check-in</button>
                        )}
                        {r.status === "in_progress" && (
                          <button
                            onClick={() => checkOut(r.id)}
                            className="text-[11px] font-bold bg-[#14213D] text-white px-2.5 py-1 rounded hover:bg-[#1F335E]"
                            data-testid={`checkout-${r.id}`}
                          >Check-out</button>
                        )}
                        {r.checked_in_at && (
                          <span className="text-[10px] text-emerald-700 font-bold" title={formatDateTimeBR(r.checked_in_at)}>✓ Check-in</span>
                        )}
                        {r.checked_out_at && (
                          <span className="text-[10px] text-[#14213D] font-bold" title={formatDateTimeBR(r.checked_out_at)}>✓ Check-out</span>
                        )}
                        {canCancel && ["pending", "pending_payment", "confirmed", "in_progress"].includes(r.status) && (
                          <button
                            onClick={() => cancelRes(r.id)}
                            className="text-[11px] font-bold border border-red-200 text-red-600 px-2.5 py-1 rounded hover:bg-red-50"
                            data-testid={`cancel-${r.id}`}
                          >Cancelar</button>
                        )}
                        {isMaster && (
                          <button
                            onClick={() => onAskDelete(r.id)}
                            title="Excluir (Master)"
                            data-testid={`delete-${r.id}`}
                            className="text-gray-300 hover:text-red-600 p-1 rounded transition-colors"
                          ><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-gray-50 border-t border-gray-100">
                      <td colSpan={9} className="px-6 py-4">
                        <ReservationDetails r={r} />
                      </td>
                    </tr>
                  )}
                </>
              );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReservationDetails({ r }) {
  // RC10 — timeline somente leitura
  const [timeline, setTimeline] = useState(null);
  useEffect(() => {
    api.get(`/reservations/${r.id}/timeline`).then(({ data }) => setTimeline(data.events)).catch(() => setTimeline([]));
  }, [r.id]);

  const address = formatAddress(r.address || r.client_address);
  const cpf = r.cpf || r.guest_cpf || r.client_cpf;
  const rg = r.rg || r.guest_rg || r.client_rg;
  const phone = r.guest_phone || r.phone || r.client_phone;
  const email = r.guest_email || r.email || r.client_email;

  const Row = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2">
      <Icon size={13} className="text-[#D4AF37] mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">{label}</div>
        <div className="text-sm text-[#14213D]">{value || <span className="text-gray-300">—</span>}</div>
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`reservation-details-${r.id}`}>
      <div className="space-y-3">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold">Cliente</div>
        <Row icon={User} label="Nome" value={r.guest_name || r.client_name} />
        <Row icon={Phone} label="Telefone" value={phone} />
        <Row icon={Mail} label="E-mail" value={email} />
        <Row icon={FileText} label="CPF" value={cpf ? maskCPF(cpf) : null} />
        <Row icon={FileText} label="RG" value={rg} />
        <Row icon={Home} label="Endereço" value={address} />
      </div>
      <div className="space-y-3">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold">Reserva</div>
        <Row icon={MapPin} label="Sala reservada" value={r.space_name} />
        <Row icon={Calendar} label="Data / Início" value={formatDateTimeBR(r.start_time)} />
        <Row icon={Clock} label="Término" value={formatDateTimeBR(r.end_time)} />
        <Row icon={Clock} label="Duração" value={formatDurationHours(r.start_time, r.end_time)} />
        <Row icon={FileText} label="Valor" value={formatBRL(r.price)} />
        <Row
          icon={FileText}
          label="Origem"
          value={r.source ? (r.source === "whatsapp-bot" ? "WhatsApp Bot" : r.source) : "site"}
        />
      </div>
      {/* RC10 — Timeline da reserva (somente leitura) */}
      <div className="md:col-span-2 border-t border-gray-200 pt-3" data-testid={`timeline-${r.id}`}>
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold mb-2">Linha do tempo</div>
        {timeline === null ? (
          <div className="text-xs text-gray-400">Carregando…</div>
        ) : timeline.length === 0 ? (
          <div className="text-xs text-gray-400">Sem eventos registrados.</div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {timeline.map((ev, i) => (
              <div key={`${ev.origin || ev.label}-${ev.at || i}`} className="flex items-center gap-2">
                {i > 0 && <span className="text-gray-300">→</span>}
                <div className="bg-white border border-gray-200 rounded-full px-3 py-1 text-[11px]">
                  <span className="font-bold text-[#14213D]">{ev.label}</span>
                  <span className="text-gray-400 ml-1.5">{formatDateTimeBR(ev.at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────── CALENDAR VIEW ──────────
const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTHS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

function CalendarView({ items, calMonth, setCalMonth }) {
  const [selected, setSelected] = useState(null);

  // Constrói matriz de dias (6 semanas × 7 dias)
  const days = useMemo(() => {
    const y = calMonth.getFullYear();
    const m = calMonth.getMonth();
    const first = new Date(y, m, 1);
    const startWeekday = first.getDay(); // 0=Dom
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    // padding start
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [calMonth]);

  // Agrupa reservas por chave YYYY-MM-DD (RC8: dia em America/Sao_Paulo)
  const byDay = useMemo(() => {
    const map = {};
    for (const r of items) {
      try {
        const key = spDateKey(r.start_time);
        (map[key] = map[key] || []).push(r);
      } catch (err) { console.warn("Data de reserva inválida ignorada:", r.id, err); }
    }
    return map;
  }, [items]);

  const keyOf = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const isToday = (dt) => {
    const t = new Date();
    return dt.getFullYear() === t.getFullYear() && dt.getMonth() === t.getMonth() && dt.getDate() === t.getDate();
  };

  const monthLabel = `${MONTHS[calMonth.getMonth()]} ${calMonth.getFullYear()}`;
  const nav = (delta) => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + delta, 1));

  const selectedList = selected ? (byDay[selected] || []) : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4" data-testid="reservas-calendar">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => nav(-1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="cal-prev" aria-label="Mês anterior">
            <ChevronLeft size={16} />
          </button>
          <div className="font-semibold text-[#14213D] text-sm" data-testid="cal-month-label">{monthLabel}</div>
          <button onClick={() => nav(1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="cal-next" aria-label="Próximo mês">
            <ChevRight size={16} />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {WEEKDAYS.map((w) => (
            <div key={w} className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold py-1">{w}</div>
          ))}
          {days.map((dt, i) => {
            if (!dt) return <div key={`empty-${i}`} className="aspect-square" />;
            const key = keyOf(dt);
            const reservs = byDay[key] || [];
            const confirmed = reservs.filter((r) => r.status === "confirmed").length;
            const pending = reservs.filter((r) => ["pending", "pending_payment"].includes(r.status)).length;
            const cancelled = reservs.filter((r) => r.status === "cancelled").length;
            const today = isToday(dt);
            const isSelected = selected === key;
            return (
              <button
                key={key}
                onClick={() => setSelected(isSelected ? null : key)}
                data-testid={`cal-day-${dt.getDate()}`}
                className={`aspect-square rounded-md text-left p-1.5 flex flex-col justify-between transition-all border ${
                  isSelected ? "border-[#14213D] bg-[#14213D]/5" :
                  today ? "border-[#D4AF37] bg-[#D4AF37]/5" :
                  reservs.length > 0 ? "border-gray-200 hover:border-[#D4AF37] bg-white" :
                  "border-transparent hover:bg-gray-50"
                }`}
              >
                <div className={`text-xs font-semibold ${today ? "text-[#D4AF37]" : "text-[#14213D]"}`}>
                  {dt.getDate()}
                </div>
                {reservs.length > 0 && (
                  <div className="flex flex-wrap gap-0.5">
                    {confirmed > 0 && (
                      <span className="text-[9px] bg-emerald-500 text-white rounded-sm px-1 leading-tight">{confirmed}</span>
                    )}
                    {pending > 0 && (
                      <span className="text-[9px] bg-amber-500 text-white rounded-sm px-1 leading-tight">{pending}</span>
                    )}
                    {cancelled > 0 && (
                      <span className="text-[9px] bg-red-400 text-white rounded-sm px-1 leading-tight">{cancelled}</span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-3 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" />Confirmada</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded-sm" />Pendente</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-400 rounded-sm" />Cancelada</span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="cal-sidepanel">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold mb-3">
          {selected ? "Reservas do dia" : "Selecione um dia"}
        </div>
        {selectedList.length === 0 && (
          <div className="text-xs text-gray-400 text-center py-6">
            {selected ? "Nenhuma reserva neste dia." : "Clique num dia para ver detalhes."}
          </div>
        )}
        <div className="space-y-2">
          {selectedList
            .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
            .map((r) => (
              <div key={r.id} className="border border-gray-200 rounded-md p-2.5" data-testid={`cal-event-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-[#14213D]">{r.space_name}</div>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-semibold ${STATUS_STYLE[r.status] || "bg-gray-100 text-gray-500"}`}>
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                </div>
                <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                  <Clock size={10} />
                  {new Date(r.start_time).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  {" – "}
                  {new Date(r.end_time).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="text-[11px] text-gray-700 mt-1 flex items-center gap-1">
                  <User size={10} />{r.guest_name || r.client_name || "—"}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
